import {
  IFoodFieldsFragment,
  INewRecipeIngredientInput,
  IUserIngredientFieldsFragment,
  useAddRecipeIngredientMutation,
  useGetRecipeQuery,
  useUpdateRecipeMutation
} from "app/gql/graphqlSchema";
import {SearchFood} from "app/food/SearchFood";
import {useParams} from "react-router";
import {assertNotNil, formatFractionPercent, isNil} from "app/util/stdlib";
import {FoodName, FoodSummary} from "app/food/FoodSummary";
import {Controller, useFieldArray, useForm} from "react-hook-form";
import {Button, ConfigProvider, Form, Input, InputNumber, notification, Tag, Tooltip, Typography} from "antd";
import {FormItem} from "react-hook-form-antd";
import {
  TwTable,
  TwTableBody,
  TwTableCell,
  TwTableFoot,
  TwTableFootRow,
  TwTableHead,
  TwTableHeader,
  TwTableRow
} from "app/components/tw";
import {DeleteOutlined, PlusOutlined} from "@ant-design/icons";
import {Calculation} from "app/recipe/Calculation";
import {Loading} from "app/components/Loading";
import {IUserIngredientModalData, UserIngredientModal} from "app/food/UserIngredientModal";
import {useState} from "react";
import {UnreachableCaseError} from "ts-essentials";
import {EditIngredientButton} from "app/food/EditIngredientButton";
import {FinalRecipe} from "app/recipe/FinalRecipe";

export const Recipe = () => {
  const {id} = useParams();
  assertNotNil(id);
  const {data, refetch: refetchGetRecipe} = useGetRecipeQuery({variables: {id}});
  const [notify, contextHolder] = notification.useNotification();
  const [isNewFoodOpen, setIsNewFoodOpen] = useState(false);
  const [userIngredientData, setUserIngredientData] = useState<IUserIngredientModalData>(null);

  const [addIngredient] = useAddRecipeIngredientMutation({
    // refetchQueries: [GetRecipeDocument]
  });

  const [updateRecipe] = useUpdateRecipeMutation({
    // refetchQueries: [GetRecipeDocument]
  });

  const recipe = data?.getRecipe;

  const {control, formState, handleSubmit} = useForm({
    values: recipe
  });

  const {fields, remove} = useFieldArray({
    control, // control props comes from useForm (optional: if you are using FormProvider)
    name: "ingredients" // unique name for your Field Array
  });

  if (isNil(recipe)) {
    return <Loading />;
  }

  const handleAddIngredient = async (ingredient: IFoodFieldsFragment | IUserIngredientFieldsFragment) => {
    let ingredientInput: Pick<INewRecipeIngredientInput, "referenceIngredient" | "userIngredient">;
    if (ingredient.__typename === "Food") {
      ingredientInput = {referenceIngredient: {foodId: ingredient.id}};
    } else if (ingredient.__typename === "UserIngredient") {
      ingredientInput = {userIngredient: {userIngredientId: ingredient.id}};
    } else {
      throw new UnreachableCaseError(ingredient);
    }
    await addIngredient({
      variables: {
        input: {
          amount: "100",
          recipeId: id,
          ...ingredientInput
        }
      }
    });
    await refetchGetRecipe();
    notify.success({
      message: "Ingredient added",
      description: `${ingredient.nameEn} added to recipe`
    });
  };

  const handleOnBlur = async () => {
    if (formState.isDirty) {
      await handleSave();
    }
  };

  const handleSave = handleSubmit(async (data) => {
    await updateRecipe({
      variables: {
        input: {
          id: data.id,
          name: data.name,
          description: data.description,
          cookedAmount: data.cookedAmount,
          ingredients: data.ingredients.map((ingredient) => ({
            ingredientId: ingredient.id,
            amount: ingredient.amount
          }))
        }
      }
    });
    await refetchGetRecipe();
  });

  const handleCreateUserIngredientFromSearch = (term: string) => {
    setUserIngredientData({defaults: {nameEn: term}});
    setIsNewFoodOpen(true);
  };

  const handleCreateUserIngredient = () => {
    setUserIngredientData(null);
    setIsNewFoodOpen(true);
  };

  const handleEditUserIngredient = (userIngredient: IUserIngredientFieldsFragment) => {
    setUserIngredientData({userIngredient});
    setIsNewFoodOpen(true);
  };

  const handleImportUserIngredient = (food: IFoodFieldsFragment) => {
    setUserIngredientData({
      defaults: {
        nameEn: food.nameEn,
        nutrition: food.nutrition
      }
    });
    setIsNewFoodOpen(true);
  };

  const handleUserIngredientModalClose = async () => {
    await refetchGetRecipe();
    setUserIngredientData(null);
    setIsNewFoodOpen(false);
  };

  return (
    <div>
      <h1 className="text-gray-600 text-2xl mb-4 flex gap-2">
        <span>
          Recipe <span className="text-gray-700 font-bold">{recipe.name}</span>
        </span>
        <span className="flex items-center">
          {!formState.isDirty ? <Tag color="success">Saved</Tag> : <Tag color="geekblue">Draft</Tag>}
        </span>
      </h1>
      <Form labelCol={{span: 4}} onFinish={handleSave} wrapperCol={{span: 20}}>
        <FormItem control={control} label="Name" name="name">
          <Input onBlur={handleOnBlur} />
        </FormItem>
        <FormItem control={control} label="Description" name="description">
          <Input.TextArea onBlur={handleOnBlur} />
        </FormItem>
        <Typography.Title level={4}>Ingredients</Typography.Title>
        <div className="flex flex-row mb-4 gap-3">
          <SearchFood
            className="flex-auto"
            onCreateNew={handleCreateUserIngredientFromSearch}
            onSelect={handleAddIngredient}
          />
          <Tooltip title="Create new ingredient">
            <Button
              color="green"
              icon={<PlusOutlined />}
              onClick={handleCreateUserIngredient}
              type="text"
              variant="solid"
            >
              Create
            </Button>
          </Tooltip>
        </div>
        <TwTable>
          <TwTableHead>
            <TwTableHeader>No.</TwTableHeader>
            <TwTableHeader>Name</TwTableHeader>
            <TwTableHeader>Quantity</TwTableHeader>
            <TwTableHeader>Actions</TwTableHeader>
          </TwTableHead>
          <TwTableBody>
            {fields.map((recipeIngredient, index) => {
              return (
                <TwTableRow key={recipeIngredient.id}>
                  <TwTableCell>
                    <div className="font-bold">{index + 1}</div>
                  </TwTableCell>
                  <TwTableCell>
                    <FoodSummary ingredient={recipeIngredient} />
                  </TwTableCell>
                  <TwTableCell>
                    <Controller
                      control={control}
                      name={`ingredients.${index}.amount`}
                      render={({field: {onChange, value}}) => (
                        <InputNumber
                          addonAfter="g"
                          changeOnWheel
                          onBlur={handleOnBlur}
                          onChange={onChange}
                          value={value}
                        />
                      )}
                    />
                  </TwTableCell>
                  <TwTableCell className="flex gap-2">
                    <EditIngredientButton
                      ingredient={recipeIngredient}
                      onEdit={handleEditUserIngredient}
                      onImport={handleImportUserIngredient}
                    />
                    <Tooltip
                      title={
                        <>
                          Remove{" "}
                          <span className="font-bold">
                            <FoodName ingredient={recipeIngredient} />
                          </span>
                        </>
                      }
                    >
                      <Button
                        color="danger"
                        icon={<DeleteOutlined />}
                        onClick={async () => {
                          remove(index);
                          await handleSave();
                        }}
                        variant="solid"
                      />
                    </Tooltip>
                  </TwTableCell>
                </TwTableRow>
              );
            })}
          </TwTableBody>
          <TwTableFoot>
            <TwTableFootRow>
              <TwTableHeader className="text-right" colSpan={2} scope="row">
                Total weight
              </TwTableHeader>
              <TwTableCell>
                <ConfigProvider
                  theme={{
                    token: {
                      colorTextDisabled: "#000"
                    }
                  }}
                >
                  <InputNumber addonAfter="g" disabled value={recipe.calculation.totalAmount} />
                </ConfigProvider>
              </TwTableCell>
              <TwTableCell />
            </TwTableFootRow>
            <TwTableFootRow>
              <TwTableHeader className="text-right" colSpan={2} scope="row">
                Weight cooked
              </TwTableHeader>
              <TwTableCell>
                <Controller
                  control={control}
                  name="cookedAmount"
                  render={({field: {onChange, value}}) => (
                    <InputNumber addonAfter="g" changeOnWheel onBlur={handleOnBlur} onChange={onChange} value={value} />
                  )}
                />
              </TwTableCell>
              <TwTableCell />
            </TwTableFootRow>
            <TwTableFootRow>
              <TwTableHeader className="text-right" colSpan={2} scope="row">
                Yield factor
              </TwTableHeader>
              <TwTableCell>{formatFractionPercent(recipe.calculation.yieldFactor)}%</TwTableCell>
              <TwTableCell />
            </TwTableFootRow>
          </TwTableFoot>
        </TwTable>
      </Form>

      <h1 className="border-b-gray-700 border-b-1 my-4 h-9 text-gray-700 text-xl">Calculation per 100g</h1>
      <Calculation calculation={recipe.calculation} />
      <FinalRecipe calculation={recipe.calculation} />
      {contextHolder}
      <UserIngredientModal
        data={userIngredientData}
        isModalOpen={isNewFoodOpen}
        notify={notify}
        onClose={handleUserIngredientModalClose}
      />
    </div>
  );
};
